import { useState, useEffect } from 'preact/hooks';
/**
 * Professional animation hook for commercial ad units
 * Optimized for conversion with subtle, staged entrance
 */
export function useCommercialAnimation(currentIndex) {
    const [contentOpacity, setContentOpacity] = useState(0); // Start hidden for entrance
    const [imageOpacity, setImageOpacity] = useState(0); // Start hidden for entrance
    const [ctaOpacity, setCtaOpacity] = useState(0); // Start hidden for entrance
    const [displayIndex, setDisplayIndex] = useState(currentIndex);
    const [isTransitioning, setIsTransitioning] = useState(true); // Start as transitioning for entrance
    const [isInitialRender, setIsInitialRender] = useState(true);
    const [lastIndex, setLastIndex] = useState(currentIndex);
    const [hasInitialized, setHasInitialized] = useState(false);
    // Initial entrance animation on mount
    useEffect(() => {
        if (hasInitialized)
            return;
        setHasInitialized(true);
        setIsTransitioning(true);
        setIsInitialRender(true);
        setTimeout(() => {
            setContentOpacity(1);
            setImageOpacity(1);
            setCtaOpacity(1);
            setIsTransitioning(false);
            setIsInitialRender(false);
        }, 80);
    }, [hasInitialized]);
    // Offer switching animation
    useEffect(() => {
        if (!hasInitialized || currentIndex === lastIndex) {
            return;
        }
        setIsTransitioning(true);
        setIsInitialRender(false);
        setContentOpacity(0);
        setImageOpacity(0);
        setCtaOpacity(0);
        setTimeout(() => {
            setDisplayIndex(currentIndex);
            setContentOpacity(1);
            setImageOpacity(1);
            setCtaOpacity(1);
            setIsTransitioning(false);
        }, 150);
        setLastIndex(currentIndex);
    }, [currentIndex, lastIndex, hasInitialized]);
    return {
        contentOpacity,
        imageOpacity,
        ctaOpacity,
        displayIndex,
        isTransitioning,
        isInitialRender,
    };
}
/**
 * Generate animation styles for commercial elements
 */
export function getCommercialStyles(opacity) {
    return {
        opacity,
        transition: 'opacity 200ms ease-out',
    };
}
/**
 * Optional: Create custom commercial animation with different timings
 */
export function useCustomCommercialAnimation(currentIndex, options) {
    const { fadeOutDuration = 200, contentDelay = 0, imageDelay = 150, ctaDelay = 250, } = options || {};
    const [contentOpacity, setContentOpacity] = useState(1);
    const [imageOpacity, setImageOpacity] = useState(1);
    const [ctaOpacity, setCtaOpacity] = useState(1);
    const [displayIndex, setDisplayIndex] = useState(currentIndex);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [lastIndex, setLastIndex] = useState(currentIndex);
    useEffect(() => {
        if (currentIndex === lastIndex)
            return;
        setIsTransitioning(true);
        // Fade out
        setContentOpacity(0);
        setImageOpacity(0);
        setCtaOpacity(0);
        // Switch content and staged fade-in with custom timing
        setTimeout(() => {
            setDisplayIndex(currentIndex);
            setTimeout(() => setContentOpacity(1), contentDelay);
            setTimeout(() => setImageOpacity(1), imageDelay);
            setTimeout(() => {
                setCtaOpacity(1);
                setIsTransitioning(false);
            }, ctaDelay);
        }, fadeOutDuration);
        setLastIndex(currentIndex);
    }, [
        currentIndex,
        lastIndex,
        fadeOutDuration,
        contentDelay,
        imageDelay,
        ctaDelay,
    ]);
    return {
        contentOpacity,
        imageOpacity,
        ctaOpacity,
        displayIndex,
        isTransitioning,
        isInitialRender: false, // Custom function doesn't handle initial render
    };
}
